import { ContentSwitcher } from "@carbon6/c6-frontend-design";
import { useState, type ReactNode } from "react";
import type {
  Dataset,
  DatasetAnalyticsResp,
  DatasetAnalyticsTrendBucket,
  TrainingGpuClusterUsage,
  TrainingGpuWorkspaceUsage,
} from "@/api";
import { formatEpisodeDuration } from "@/components/dataset/datasetFormatters";
import type { LoadState } from "@/components/dataset/loadState";
import { GpuUsageStrip, gpuUsagePeriodLabel } from "@/components/gpu/usage";
import { ChartCard, StatTile } from "@/components/metric";
import { EmptyState } from "@/shared/ui/emptyState/public";
import { LoadingState } from "@/shared/ui/loadingState/public";
import { RecentDatasetsCard } from "./RecentDatasetsCard";
import { TopDurationDatasetsCard } from "./TopDurationDatasetsCard";
import { DASHBOARD_GPU_USAGE_PERIOD } from "./useDashboardGpuUsage";
import { formatBytes, formatNumber } from "@/utils";
import { formatDate, formatDuration } from "@/utils/datetime";
import styles from "./DataDashboardBody.module.css";
import {
  armLayoutText,
  dataOriginText,
  foldedCount,
  foldTail,
  labelAxisText,
  recordingModeText,
  type HeatmapModel,
  summarizeTrend,
  toCameraStreamData,
  toCategoryData,
  toOperatorData,
  toQuantileSummary,
  toShareSegments,
  toTaskEmbodimentHeatmap,
  toTrendSeries,
  type TrendMetric,
} from "./dataDashboardModel";
import {
  type CategoryDatum,
  CountBarChart,
  CountColumnChart,
  CountHeatmap,
  QuantileRangeStrip,
  ShareDonutChart,
  type ShareSegment,
  TrendAreaChart,
} from "./charts";

// 부분-전체 축의 고정 vocab 순서. 이 순서로 색 슬롯을 정한다. 서버 배열은 개수 순이라 그 순서를
// 그대로 쓰면 데이터가 바뀔 때 같은 값의 색이 바뀐다.
const RECORDING_MODE_VOCAB = ["teleoperation", "direct_teaching"];
const DATA_ORIGIN_VOCAB = ["policy_rollout", "human_demonstration"];
const ARM_LAYOUT_VOCAB = ["single_arm", "bimanual"];
const ENVIRONMENT_VOCAB = ["real", "simulation"];
// 출처 allowlist 는 서버 계약이 SoT 다. 여기 순서는 색 슬롯을 고정하는 용도일 뿐이다. 계약에 새
// 값이 먼저 들어오면 `toShareSegments` 가 뒤에 추가한다.
const SOURCE_VOCAB = ["open_source", "internal", "alba", "customer"];

const bucketOptions = [
  { value: "week", label: "주" },
  { value: "day", label: "일" },
] as const;

const trendMetricOptions: readonly { value: TrendMetric; label: string }[] = [
  { value: "count", label: "개수" },
  { value: "episodes", label: "에피소드" },
  { value: "duration", label: "시간" },
  { value: "size", label: "용량" },
];

function countText(count: number): string {
  return `${formatNumber(count)}개`;
}

// 평균은 소수 첫째 자리까지 남긴다. 정수로 반올림하면 값이 작은 축이 "평균 0개" 가 된다.
function averageCountText(value: number): string {
  return `${formatNumber(Math.round(value * 10) / 10)}개`;
}

// 지표별 표기. 시리즈 값은 모델(toTrendSeries)이 고르고, 여기서는 문구와 포맷만 정한다.
const TREND_METRIC_VIEW: Readonly<
  Record<
    TrendMetric,
    {
      seriesLabel: string;
      format: (value: number) => string;
      formatAverage: (value: number) => string;
    }
  >
> = {
  count: { seriesLabel: "생성", format: countText, formatAverage: averageCountText },
  episodes: { seriesLabel: "에피소드", format: countText, formatAverage: averageCountText },
  duration: {
    seriesLabel: "수집 길이",
    format: (seconds) => formatDuration(seconds * 1000),
    formatAverage: (seconds) => formatDuration(seconds * 1000),
  },
  size: { seriesLabel: "용량", format: formatBytes, formatAverage: formatBytes },
};

// 요약 줄의 기간 어휘. 선택된 버킷을 따른다.
const TREND_PERIOD_TEXT: Readonly<
  Record<DatasetAnalyticsTrendBucket, { current: string; previous: string; unit: string }>
> = {
  week: { current: "이번 주", previous: "지난주", unit: "주" },
  day: { current: "오늘", previous: "어제", unit: "일" },
};

function durationText(seconds: number): string {
  return formatEpisodeDuration(seconds) ?? "—";
}

function rateText(hz: number): string {
  return `${Math.round(hz * 10) / 10}Hz`;
}

// 접힌 값이 있으면 카드 부제로 알린다. 사용자가 상위 N 만 보고 전체로 오해하지 않게 한다.
function foldNote(data: readonly CategoryDatum[]): string | undefined {
  const folded = foldedCount(data);
  return folded === 0 ? undefined : `상위 8종만 표시하고 나머지 ${folded}종은 기타로 묶었습니다.`;
}

// 히트맵 부제. 모집단과 복수 라벨 의미는 항상 표시하고, 접힘 안내는 접혔을 때만 표시한다.
function heatmapSubtitle(model: HeatmapModel | null): string {
  const base =
    "task 라벨이 있는 Dataset만 셉니다. 한 Dataset이 여러 task 를 가질 수 있어 " +
    "셀 합이 Dataset 수보다 클 수 있습니다.";
  if (model === null || model.foldedTaskCount === 0) {
    return base;
  }
  return `${base} 상위 8개 작업만 표시하고 나머지 ${model.foldedTaskCount}종은 기타로 묶었습니다.`;
}

function BarCard({
  title,
  data,
  labelWidth,
}: {
  title: string;
  data: readonly CategoryDatum[];
  labelWidth?: number;
}) {
  if (data.length === 0) {
    return <ChartCard title={title} empty="이 축에 집계할 값이 아직 없습니다." />;
  }
  return (
    <ChartCard title={title} subtitle={foldNote(data)}>
      <CountBarChart data={foldTail(data)} formatCount={countText} labelWidth={labelWidth} />
    </ChartCard>
  );
}

// 축별 도넛을 한 카드에 나란히 둔다. 조각이 하나도 없는 축은 도넛을 만들지 않는다.
function DonutAxesCard({
  title,
  subtitle,
  axes,
}: {
  title: string;
  subtitle?: string;
  axes: readonly { key: string; title: string; segments: ShareSegment[] }[];
}) {
  const drawn = axes.filter((axis) => axis.segments.length > 0);
  if (drawn.length === 0) {
    return <ChartCard title={title} empty="이 축에 집계할 값이 아직 없습니다." />;
  }
  return (
    <ChartCard title={title} subtitle={subtitle}>
      <div className={styles.donutRow}>
        {drawn.map((axis) => (
          <ShareDonutChart
            key={axis.key}
            title={axis.title}
            segments={axis.segments}
            formatCount={countText}
          />
        ))}
      </div>
    </ChartCard>
  );
}

// 총량 KPI 행. 통계 분기의 맨 위에 따로 둔다. GPU 구역과 목록 행이 KPI 와 나머지 카드 사이에
// 있기 때문이다.
function KpiRow({ totals }: { totals: DatasetAnalyticsResp["totals"] }): ReactNode {
  return (
    <div className={styles.kpiRow}>
      <StatTile
        label="Dataset"
        value={formatNumber(totals.dataset_count)}
        hint="사용 가능"
        emphasis
      />
      <StatTile
        label="리뷰 완료"
        value={formatNumber(totals.reviewed_count)}
        hint={`전체의 ${totals.dataset_count === 0 ? 0 : Math.round((totals.reviewed_count / totals.dataset_count) * 100)}%`}
        ratio={totals.dataset_count === 0 ? 0 : totals.reviewed_count / totals.dataset_count}
      />
      <StatTile label="에피소드" value={formatNumber(totals.episode_count)} />
      <StatTile label="총 수집 길이" value={formatDuration(totals.total_duration_sec * 1000)} />
      <StatTile label="총 용량" value={formatBytes(totals.total_size_bytes)} />
    </div>
  );
}

function AnalyticsCards({
  analytics,
  bucket,
  onBucketChange,
}: {
  analytics: DatasetAnalyticsResp;
  bucket: DatasetAnalyticsTrendBucket;
  onBucketChange: (bucket: DatasetAnalyticsTrendBucket) => void;
}): ReactNode {
  const [trendMetric, setTrendMetric] = useState<TrendMetric>("count");
  const metricView = TREND_METRIC_VIEW[trendMetric];
  const trend = toTrendSeries(analytics.created_trend, analytics.trend_bucket, trendMetric);
  // 요약의 "이번" 은 벽시계 기준이다. 렌더링 시점의 now 로 충분하고 초 단위 정확도는 필요 없다.
  const trendSummary = summarizeTrend(trend, analytics.trend_bucket, Date.now());
  const periodText = TREND_PERIOD_TEXT[analytics.trend_bucket];

  const embodiment = toCategoryData(analytics.embodiment_counts);
  const tip = toCategoryData(analytics.tip_counts, { unknownAxis: "tip" });
  const operator = toOperatorData(analytics.operator_counts);
  const task = toCategoryData(analytics.labels.task, { formatValue: labelAxisText("task") });
  const cameraStream = toCameraStreamData(analytics.camera_stream_counts);
  const episodeLength = toQuantileSummary(analytics.episode_length_sec);
  const primaryRate = toQuantileSummary(analytics.primary_rate_hz);
  const heatmap = toTaskEmbodimentHeatmap(analytics.task_embodiment_matrix);

  return (
    <>
      <ChartCard
        title="수집 추이"
        subtitle="Dataset이 만들어진 시점 기준입니다."
        action={
          <div className={styles.trendControls}>
            <ContentSwitcher
              label="수집 추이 지표"
              size="sm"
              options={[...trendMetricOptions]}
              value={trendMetric}
              onValueChange={(value) => setTrendMetric(value as TrendMetric)}
            />
            <ContentSwitcher
              label="수집 추이 버킷"
              size="sm"
              options={[...bucketOptions]}
              value={bucket}
              onValueChange={(value) => onBucketChange(value as DatasetAnalyticsTrendBucket)}
            />
          </div>
        }
        empty={trend.length === 0 ? "아직 집계할 수집 기록이 없습니다." : undefined}
      >
        {trendSummary === null ? null : (
          <p className={styles.trendSummary}>
            {`${periodText.current} ${metricView.format(trendSummary.current)} (진행 중) · ` +
              `${periodText.previous} ${metricView.format(trendSummary.previous)}` +
              (trendSummary.recentSpan < 2
                ? ""
                : ` · 최근 ${trendSummary.recentSpan}${periodText.unit} 평균 ` +
                  metricView.formatAverage(trendSummary.recentAverage))}
          </p>
        )}
        <TrendAreaChart
          data={trend}
          formatTick={(atMs) => formatDate(atMs)}
          formatValue={metricView.format}
          seriesLabel={metricView.seriesLabel}
        />
      </ChartCard>

      <div className={styles.cardRow}>
        {/* embodiment 이름(`sigma_arm_sigma_hand_1.0` 류)과 팁 이름이 길어서 라벨 열을 넓힌다. */}
        <BarCard title="Embodiment" data={embodiment} labelWidth={160} />
        <BarCard title="Tip" data={tip} labelWidth={160} />
      </div>

      <div className={styles.cardRow}>
        <BarCard title="담당자" data={operator} labelWidth={140} />
        <BarCard title="작업(task) 라벨" data={task} labelWidth={140} />
      </div>

      <div className={styles.cardRow}>
        <DonutAxesCard
          title="수집 구성"
          axes={[
            {
              key: "recording_mode",
              title: "수집 방식",
              segments: toShareSegments(analytics.recording_mode_counts, RECORDING_MODE_VOCAB, {
                formatValue: recordingModeText,
              }),
            },
            {
              key: "data_origin",
              title: "행동 주체",
              segments: toShareSegments(analytics.data_origin_counts, DATA_ORIGIN_VOCAB, {
                formatValue: dataOriginText,
              }),
            },
            {
              key: "arm_layout",
              title: "팔 구성",
              segments: toShareSegments(analytics.arm_layout_counts, ARM_LAYOUT_VOCAB, {
                formatValue: armLayoutText,
              }),
            },
          ]}
        />
        <DonutAxesCard
          title="라벨 구성"
          axes={[
            {
              key: "environment",
              title: "환경",
              segments: toShareSegments(analytics.labels.environment, ENVIRONMENT_VOCAB, {
                formatValue: labelAxisText("environment"),
              }),
            },
            {
              key: "source",
              title: "출처",
              segments: toShareSegments(analytics.labels.source, SOURCE_VOCAB, {
                formatValue: labelAxisText("source"),
              }),
            },
          ]}
        />
      </div>

      <div className={styles.cardRow}>
        <ChartCard
          title="카메라 스트림 수"
          subtitle="Dataset이 실제로 담은 카메라 채널 수입니다."
          empty={cameraStream.length === 0 ? "이 축에 집계할 값이 아직 없습니다." : undefined}
        >
          {/* 카드 제목이 이미 대상을 알려 주므로 차트 눈금에는 단위만 적는다. */}
          <CountColumnChart data={cameraStream} formatCount={countText} axisLabel="채널 수" />
        </ChartCard>
        {/* "Frequency" 는 카메라 fps 로도, 제어 주기로도 읽히는 중립 용어라 선택했다. 값의
            출처(데이터셋이 선언한 프레임레이트 중앙값)는 부제가 설명한다. */}
        <ChartCard
          title="에피소드 길이·Frequency 분포"
          subtitle="Frequency 는 Dataset마다 선언한 프레임레이트(fps)의 중앙값입니다."
          empty={
            episodeLength === null && primaryRate === null
              ? "요약할 표본이 아직 없습니다."
              : undefined
          }
        >
          <QuantileRangeStrip
            title="에피소드 길이"
            summary={episodeLength}
            formatValue={durationText}
          />
          <QuantileRangeStrip title="Frequency" summary={primaryRate} formatValue={rateText} />
        </ChartCard>
      </div>

      {/* 커버리지 매트릭스. "다음에 무엇을 모을지" 를 정하는 이 페이지의 핵심 입력이라 전체 폭을 쓴다. */}
      <ChartCard
        title="작업 × Embodiment"
        subtitle={heatmapSubtitle(heatmap)}
        empty={heatmap === null ? "task 라벨이 부여된 Dataset이 아직 없습니다." : undefined}
      >
        {heatmap === null ? null : (
          <CountHeatmap
            rows={heatmap.rows}
            columns={heatmap.columns}
            data={heatmap.data}
            formatCount={countText}
            caption="작업별 Embodiment Dataset 분포"
          />
        )}
      </ChartCard>
    </>
  );
}

/**
 * Data Dashboard 페이지 본문.
 *
 * 루트는 여백을 가지지 않는다. 셸이 인셋을 소유하므로 행 간격만 `gap` 으로 만든다
 * (`docs/rules.md` §14).
 */
export function DataDashboardBody({
  state,
  bucket,
  onBucketChange,
  onRetry,
  recentState,
  onRecentRetry,
  onThumbnailStale,
  topDurationState,
  onTopDurationRetry,
  onTopDurationThumbnailStale,
  gpuUsage,
  gpuCluster,
  onGpuRetry,
}: {
  state: LoadState<DatasetAnalyticsResp>;
  bucket: DatasetAnalyticsTrendBucket;
  onBucketChange: (bucket: DatasetAnalyticsTrendBucket) => void;
  onRetry: () => void;
  recentState: LoadState<readonly Dataset[]>;
  onRecentRetry: () => void;
  onThumbnailStale: () => void;
  topDurationState: LoadState<readonly Dataset[]>;
  onTopDurationRetry: () => void;
  onTopDurationThumbnailStale: () => void;
  gpuUsage: LoadState<TrainingGpuWorkspaceUsage>;
  gpuCluster: LoadState<TrainingGpuClusterUsage>;
  // GPU 조회 둘만 다시 실행한다. workspace 사용량 실패 안내와 띠의 추세 카드가 함께 쓴다.
  onGpuRetry: () => void;
}) {
  return (
    <div className={styles.page}>
      <div className={styles.scopeNotes}>
        <p className={styles.scopeNote}>Dataset 수치는 사용 가능한 Dataset만 집계합니다.</p>
        <p className={styles.scopeNote}>
          {`GPU 수치는 ${gpuUsagePeriodLabel(DASHBOARD_GPU_USAGE_PERIOD)} 기준입니다.`}
        </p>
      </div>
      {state.status === "error" ? (
        <EmptyState tone="error" action={{ label: "다시 시도", onClick: onRetry }}>
          {state.message}
        </EmptyState>
      ) : state.status === "success" ? (
        <KpiRow totals={state.data.totals} />
      ) : (
        <LoadingState>통계를 불러오고 있습니다.</LoadingState>
      )}
      {/* GPU 구역과 목록 행은 통계 분기 밖에 둔다. 한 조회의 실패가 다른 구역을 가리지 않는다. */}
      <div className={styles.gpuUsage}>
        {/* 띠는 workspace 사용량의 실패를 알리지 않는다. 그 타일은 `—` 로 남는다. 그래서 여기서
            알린다. 클러스터 점유의 실패는 띠의 추세 카드가 알린다. */}
        {gpuUsage.status === "error" ? (
          <EmptyState size="sm" tone="error" action={{ label: "다시 시도", onClick: onGpuRetry }}>
            {gpuUsage.message}
          </EmptyState>
        ) : null}
        <GpuUsageStrip
          usage={gpuUsage.status === "success" ? gpuUsage.data : null}
          cluster={gpuCluster}
          onRetry={onGpuRetry}
        />
      </div>
      <div className={styles.listRow}>
        <RecentDatasetsCard
          state={recentState}
          onRetry={onRecentRetry}
          onThumbnailStale={onThumbnailStale}
        />
        <TopDurationDatasetsCard
          state={topDurationState}
          onRetry={onTopDurationRetry}
          onThumbnailStale={onTopDurationThumbnailStale}
        />
      </div>
      {state.status === "success" ? (
        <AnalyticsCards analytics={state.data} bucket={bucket} onBucketChange={onBucketChange} />
      ) : null}
    </div>
  );
}
